import React, { useState } from 'react';
import {
  BuildingStorefrontIcon, CheckCircleIcon, XCircleIcon,
  EyeIcon, PencilSquareIcon, CurrencyRupeeIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useMutation, usePagination } from '../../hooks';
import { businessAPI } from '../../services/api';
import {
  Card, SectionHeader, SearchInput, Button, Badge, StatusBadge,
  Modal, Input, Tabs, LoadingPage, EmptyState, Pagination,
  ConfirmDialog,
} from '../../components/common/UI';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const STATUS_TABS = [
  { value: '',               label: 'All'      },
  { value: 'pending_review', label: 'Pending'  },
  { value: 'approved',       label: 'Approved' },
  { value: 'suspended',      label: 'Suspended'},
  { value: 'rejected',       label: 'Rejected' },
];

const STATUS_BADGE = { approved:'green', pending_review:'yellow', suspended:'red', rejected:'red' };

// ── Business detail ────────────────────
const BusinessModal = ({ biz, open, onClose, onAction }) => {
  const [rejNote, setRejNote] = useState('');
  const { mutate: approve,  loading: approving  } = useMutation(() => businessAPI.approve(biz.id),        { successMsg: 'Business approved', onSuccess: onAction });
  const { mutate: suspend,  loading: suspending  } = useMutation(() => businessAPI.suspend(biz.id, { reason: rejNote }), { successMsg: 'Business suspended', onSuccess: onAction });
  const { mutate: reject,   loading: rejecting   } = useMutation(() => businessAPI.reject(biz.id,  { reason: rejNote }), { successMsg: 'Business rejected',  onSuccess: onAction });
  const { register: regComm, handleSubmit: hComm } = useForm({ defaultValues: { commission_rate: biz?.commission_rate || 10 } });
  const { mutate: setComm, loading: settingComm } = useMutation(
    (d) => businessAPI.commission(biz.id, d),
    { successMsg: 'Commission updated', onSuccess: onAction }
  );

  if (!biz) return null;
  return (
    <Modal open={open} onClose={onClose} title="Business Details" size="lg">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start gap-4">
          {biz.logo_url
            ? <img src={biz.logo_url} alt={biz.name} className="h-14 w-14 rounded-2xl object-cover" />
            : <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-100 text-brand-700 font-bold text-xl">{biz.name?.[0]}</div>
          }
          <div className="flex-1">
            <h3 className="font-display font-bold text-surface-900">{biz.name}</h3>
            <p className="text-sm text-surface-500">{biz.business_type}</p>
            <div className="mt-1 flex gap-2">
              <Badge variant={STATUS_BADGE[biz.status] || 'gray'}>{biz.status?.replace(/_/g,' ')}</Badge>
            </div>
          </div>
        </div>

        {/* Info grid */}
        <div className="grid grid-cols-2 gap-3 rounded-xl bg-surface-50 p-4 text-sm">
          <div><p className="text-surface-400 text-xs">GST</p><p className="font-mono font-medium mt-0.5">{biz.gst_number || '—'}</p></div>
          <div><p className="text-surface-400 text-xs">PAN</p><p className="font-mono font-medium mt-0.5">{biz.pan_number || '—'}</p></div>
          <div><p className="text-surface-400 text-xs">City / State</p><p className="font-medium mt-0.5">{biz.city}, {biz.state}</p></div>
          <div><p className="text-surface-400 text-xs">Contact</p><p className="font-medium mt-0.5">{biz.support_phone || biz.support_email || '—'}</p></div>
          <div><p className="text-surface-400 text-xs">Registered</p><p className="font-medium mt-0.5">{biz.created_at ? format(new Date(biz.created_at), 'dd MMM yyyy') : '—'}</p></div>
          <div><p className="text-surface-400 text-xs">Return Policy</p><p className="font-medium mt-0.5">{biz.return_policy_days} days</p></div>
        </div>

        {biz.description && <p className="text-sm text-surface-600">{biz.description}</p>}

        {/* Commission */}
        <div className="rounded-xl border border-brand-100 bg-brand-50 p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-brand-700 mb-3">Platform Commission</p>
          <form onSubmit={hComm(setComm)} className="flex items-end gap-3">
            <Input label="Commission %" type="number" min={0} max={50} step={0.5} className="w-32"
              {...regComm('commission_rate', { required: true, min: 0, max: 50 })} />
            <Button type="submit" variant="primary" size="sm" loading={settingComm}>Update</Button>
          </form>
        </div>

        {/* Rejection note */}
        {(biz.status === 'pending_review' || biz.status === 'approved') && (
          <div>
            <Input label="Note (for rejection/suspension)" value={rejNote} onChange={e => setRejNote(e.target.value)} placeholder="Optional reason…" />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-1">
          {biz.status === 'pending_review' && <>
            <Button variant="primary"    size="sm" icon={CheckCircleIcon} loading={approving} onClick={approve}>Approve</Button>
            <Button variant="danger"     size="sm" icon={XCircleIcon}     loading={rejecting}  onClick={reject}>Reject</Button>
          </>}
          {biz.status === 'approved' && <Button variant="danger" size="sm" loading={suspending} onClick={suspend}>Suspend</Button>}
          {biz.status === 'suspended' && <Button variant="primary" size="sm" loading={approving} onClick={approve}>Re-activate</Button>}
        </div>
      </div>
    </Modal>
  );
};

// ── Main page ─────────────────────────────────────────
export default function BusinessesPage() {

  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const { page, setPage, reset } = usePagination();

  const { data, loading, refetch } = useQuery(
    () => businessAPI.adminList({ page, limit: 20, status: statusFilter || undefined }),
    [page, statusFilter]
  );

  const businesses = Array.isArray(data)
    ? data
    : (data?.rows || data?.data?.rows || (Array.isArray(data?.data) ? data.data : Array.isArray(data?.businesses) ? data.businesses : []));
  const total      = data?.count ?? data?.total ?? data?.pagination?.total ?? data?.data?.count ?? (Array.isArray(businesses) ? businesses.length : 0);
  const pages      = (data?.pagination?.pages ?? Math.ceil(total / 20)) || 1;

  return (
    <div className="animate-in space-y-5">
      <SectionHeader title="Business Management" subtitle={`${total} businesses`} />

      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="w-full sm:w-auto">
            <Tabs tabs={STATUS_TABS} active={statusFilter} onChange={v => { setStatusFilter(v); reset(); }} />
          </div>
          <SearchInput value={search} onChange={setSearch} className="flex-1 min-w-[200px]" />
        </div>

        {loading ? <LoadingPage /> : businesses.length === 0 ? (
          <EmptyState icon={BuildingStorefrontIcon} title="No businesses found" />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Business</th><th>Type</th><th>Location</th><th>Commission</th><th>Status</th><th>Joined</th><th></th></tr></thead>
              <tbody>
                {businesses.map(b => (
                  <tr key={b.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        {b.logo_url
                          ? <img src={b.logo_url} alt={b.name} className="h-8 w-8 rounded-lg object-cover flex-shrink-0" />
                          : <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-700 font-bold text-sm">{b.name?.[0]}</div>
                        }
                        <div>
                          <p className="font-medium text-surface-900">{b.name}</p>
                          <p className="text-xs text-surface-400">/{b.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-surface-500 text-sm">{b.business_type || '—'}</td>
                    <td className="text-surface-500 text-sm">{b.city || '—'}, {b.state || '—'}</td>
                    <td><span className="font-mono text-sm font-medium">{b.commission_rate}%</span></td>
                    <td><Badge variant={STATUS_BADGE[b.status] || 'gray'}>{b.status?.replace(/_/g,' ')}</Badge></td>
                    <td className="text-surface-400 text-xs">{b.created_at ? format(new Date(b.created_at), 'dd MMM yy') : '—'}</td>
                    <td>
                      <button className="btn btn-ghost p-1.5 rounded-lg" onClick={() => setSelected(b)}>
                        <EyeIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} pages={pages} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <BusinessModal
        biz={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onAction={() => { refetch(); setSelected(null); }}
      />
    </div>
  );
}
